// GuildCommandCenter.tsx
// Renders on the authenticated Home page, above the "Next Raid" hero.
// One compact, single-line row per guild that the user owns or officers,
// with one-click shortcuts that deep-link straight into the relevant
// Dashboard sub-tab on GuildsPage. The Release Players controls sit on the
// same line, offset to the right with a subtle divider so it reads as a
// distinct action area, and the Guild Ready checklist rides underneath inside
// the same shell.
//
// Returns null when the user manages no guilds, so players who are neither
// officer nor owner see no extra UI.
//
// All complex dashboard logic lives in GuildDashboard.tsx and remains
// untouched. This component only sets sessionStorage flags and navigates to
// `#Guilds`; GuildsPage and GuildDashboard pick up those flags on mount.
import React, { useEffect, useRef, useState } from "react";
import { GuildWeeklyRelease } from "./GuildWeeklyRelease";
import GuildReadyBar from "./GuildReadyBar";

export type ManagedGuild = {
  id: string;
  name: string;
  tag: string | null;     // kept on type for callers; not displayed in this component
  logoUrl: string | null;
  isOwner: boolean;
  isOfficer: boolean;
};

type SubtabKey =
  | "overview"
  | "raids"
  | "roster"
  | "squads"
  | "attendance"
  | "loot"
  | "ledger"
  | "consumables"
  | "requests";

const navigateToGuild = (
  guildId: string,
  detailTab: "info" | "dashboard",
  subtab?: SubtabKey,
  openSettings?: boolean
) => {
  sessionStorage.setItem("openGuildId", guildId);
  sessionStorage.setItem("openGuildDetailTab", detailTab);
  if (subtab) sessionStorage.setItem("openDashboardSubtab", subtab);
  if (openSettings) sessionStorage.setItem("openGuildSettingsModal", "1");
  window.location.hash = "Guilds";
};

const Icons = {
  Overview: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Raids: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  Roster: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  Squads: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Attendance: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Flame: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  ),
  Scroll: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Ledger: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  ),
  Beaker: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  Inbox: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-5l-2 3h-2l-2-3H4" />
    </svg>
  ),
  Chevron: () => (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  Settings: () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

// Flat top-level shortcuts, split around the Members dropdown:
// Overview · Raids · [Members ▾] · Requests · [Heat ▾] · Settings
const LEFT_SHORTCUTS: { key: SubtabKey; label: string; Icon: React.ComponentType }[] = [
  { key: "overview",   label: "Overview",   Icon: Icons.Overview },
  { key: "raids",      label: "Raids",      Icon: Icons.Raids },
];
const RIGHT_SHORTCUTS: { key: SubtabKey; label: string; Icon: React.ComponentType }[] = [
  { key: "requests",   label: "Requests",   Icon: Icons.Inbox },
];

type MenuItem = { key: SubtabKey; label: string; hint: string; Icon: React.ComponentType };

// People management grouped under one dropdown so the row keeps fitting as
// features grow: who's in the guild, how they're grouped, and who shows up.
const MEMBER_ITEMS: MenuItem[] = [
  { key: "roster",     label: "Roster",     hint: "Members, specs, availability",  Icon: Icons.Roster },
  { key: "squads",     label: "Squads",     hint: "Sub-groups within the guild",   Icon: Icons.Squads },
  { key: "attendance", label: "Attendance", hint: "Raid attendance history",       Icon: Icons.Attendance },
];

// Heat economy tools grouped under one dropdown: everything that feeds or
// spends heat (loot imports and awards, consumable rewards).
const HEAT_ITEMS: MenuItem[] = [
  { key: "loot",        label: "Loot Imports", hint: "RCLC awards (spend heat)",       Icon: Icons.Scroll },
  { key: "ledger",      label: "Loot Ledger",  hint: "Award history and revokes",      Icon: Icons.Ledger },
  { key: "consumables", label: "Consumables",  hint: "WCL reports (earn heat)",        Icon: Icons.Beaker },
];

/** Dropdown nav group: closes on outside click or Escape. Amber accent for
 *  Heat, neutral for everything else so it blends with the flat buttons. */
function NavMenu({ guildId, label, Icon, items, accent = "neutral" }: {
  guildId: string;
  label: string;
  Icon: React.ComponentType;
  items: MenuItem[];
  accent?: "neutral" | "amber";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const buttonClass = accent === "amber"
    ? (open
        ? "text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/25"
        : "text-amber-700/70 dark:text-amber-300/70 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-500/10 border border-transparent")
    : (open
        ? "text-[var(--c-ink)] bg-[var(--c-sunken)] border border-[var(--c-line-strong)]"
        : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] border border-transparent");
  const itemIconClass = accent === "amber"
    ? "text-amber-700/80 dark:text-amber-300/80 group-hover:text-amber-800 dark:group-hover:text-amber-200"
    : "text-[var(--c-ink-3)] group-hover:text-[var(--c-ink)]";

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-all ${buttonClass}`}
      >
        <Icon />
        <span>{label}</span>
        <span className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <Icons.Chevron />
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-xl shadow-black/60 p-1">
          {items.map(({ key, label: itemLabel, hint, Icon: I }) => (
            <button
              key={key}
              onClick={() => {
                setOpen(false);
                navigateToGuild(guildId, "dashboard", key);
              }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-[var(--c-sunken)] transition-colors group"
            >
              <span className={itemIconClass}><I /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] font-semibold text-[var(--c-ink)] group-hover:text-[var(--c-ink)]">{itemLabel}</span>
                <span className="block text-[10px] text-[var(--c-ink-3)] truncate">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GuildCommandCenter({ guilds }: { guilds: ManagedGuild[] }) {
  // Hide entirely for users with no managed guilds — keeps the Home page
  // clean for regular players.
  if (!guilds || guilds.length === 0) return null;

  return (
    <div className="space-y-2">
      {guilds.map((g) => (
        <div
          key={g.id}
          className="rounded-2xl border border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] hover:border-[var(--c-line)] transition-colors px-3 py-2.5 sm:px-4"
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Logo + name (kept paired so they never split across wraps) */}
          <div className="flex items-center gap-2.5 min-w-0 flex-shrink-0">
            {g.logoUrl ? (
              <img
                src={g.logoUrl}
                alt=""
                className="w-8 h-8 rounded-lg border border-[var(--c-line)] object-cover bg-[var(--c-sunken-2)]"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <div className="w-8 h-8 rounded-lg border border-[var(--c-line)] bg-gradient-to-br from-indigo-500/20 to-cyan-500/10 flex items-center justify-center text-xs font-bold text-[var(--c-ink-2)]">
                {g.name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-sm font-bold text-[var(--c-ink)] truncate">{g.name}</span>
          </div>

          {/* Dashboard shortcuts: flat buttons plus two grouped dropdowns
              (Members, Heat) so the row stays on one line as features grow. */}
          <div className="flex flex-wrap items-center gap-0.5">
            {LEFT_SHORTCUTS.map(({ key, label, Icon: I }) => (
              <button
                key={key}
                onClick={() => navigateToGuild(g.id, "dashboard", key)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-all"
              >
                <I />
                <span>{label}</span>
              </button>
            ))}
            <NavMenu guildId={g.id} label="Members" Icon={Icons.Roster} items={MEMBER_ITEMS} />
            {RIGHT_SHORTCUTS.map(({ key, label, Icon: I }) => (
              <button
                key={key}
                onClick={() => navigateToGuild(g.id, "dashboard", key)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-all"
              >
                <I />
                <span>{label}</span>
              </button>
            ))}
            <NavMenu guildId={g.id} label="Heat" Icon={Icons.Flame} items={HEAT_ITEMS} accent="amber" />
            {g.isOwner && (
              <button
                onClick={() => navigateToGuild(g.id, "info", undefined, true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-all"
                title="Edit guild settings"
              >
                <Icons.Settings />
                <span>Settings</span>
              </button>
            )}
          </div>

          {/* Release Players — same line, pushed right with a subtle divider so
              it reads as a distinct action area. The divider hides on narrow
              screens where the row has wrapped anyway. */}
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            <span className="hidden sm:inline-block w-px h-5 bg-[var(--c-sunken-2)]" />
            <GuildWeeklyRelease guildId={g.id} context="home" />
          </div>
          </div>

          {/* Guild Ready rides inside the same shell as a second line, the way
              the player's bar nests its lockout rail. Its rows point at
              controls that live on the guild page, so it gets a way to take
              the officer there before the ring lands. */}
          <GuildReadyBar
            guildId={g.id}
            variant="inline"
            onOpenDashboard={(tab) => navigateToGuild(g.id, "dashboard", tab as SubtabKey)}
            onOpenGuildPage={() => navigateToGuild(g.id, "info")}
          />
        </div>
      ))}
    </div>
  );
}
